import React, { useCallback, useEffect, useRef, useState } from 'react';
import { lockBodyScroll } from '../utils/bodyScrollLock';
import { RIDER_THEME } from '../pages/rider/RiderV2Constants';

let html5QrcodeLib = null;
let html5QrcodeLoadingPromise = null;

function loadHtml5Qrcode() {
  if (html5QrcodeLib) return Promise.resolve(html5QrcodeLib);
  if (html5QrcodeLoadingPromise) return html5QrcodeLoadingPromise;
  if (typeof window !== 'undefined' && window.Html5Qrcode) {
    html5QrcodeLib = window.Html5Qrcode;
    return Promise.resolve(html5QrcodeLib);
  }
  html5QrcodeLoadingPromise = new Promise((resolve, reject) => {
    if (!document.querySelector('script[data-qr-scanner]')) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/html5-qrcode@2.3.8/html5-qrcode.min.js';
      script.async = true;
      script.defer = true;
      script.dataset.qrScanner = 'true';
      script.onload = () => {
        if (window.Html5Qrcode) {
          html5QrcodeLib = window.Html5Qrcode;
          resolve(html5QrcodeLib);
        } else {
          reject(new Error('Html5Qrcode failed to load'));
        }
      };
      script.onerror = () => reject(new Error('Network error loading QR scanner'));
      document.head.appendChild(script);
    } else {
      const checkInterval = setInterval(() => {
        if (window.Html5Qrcode) {
          clearInterval(checkInterval);
          html5QrcodeLib = window.Html5Qrcode;
          resolve(html5QrcodeLib);
        }
      }, 100);
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!window.Html5Qrcode) reject(new Error('QR scanner load timeout'));
      }, 10000);
    }
  });
  return html5QrcodeLoadingPromise;
}

function QrScannerMount({ onResult, onError, continuous }) {
  const containerRef = useRef(null);
  const scannerRef = useRef(null);
  const startedRef = useRef(false);
  const lockedRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    const elementId = 'qr-scanner-mount-' + Date.now();
    if (containerRef.current) containerRef.current.id = elementId;

    loadHtml5Qrcode()
      .then((Html5Qrcode) => {
        if (!mounted || !containerRef.current) return;
        const scanner = new Html5Qrcode(elementId, { verbose: false });
        scannerRef.current = scanner;
        return scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => {
            if (!decodedText || lockedRef.current) return;
            lockedRef.current = true;
            Promise.resolve(onResult(decodedText)).finally(() => {
              if (continuous) {
                setTimeout(() => { lockedRef.current = false; }, 900);
              }
            });
          },
          () => {},
        ).then(() => {
          if (mounted) startedRef.current = true;
        });
      })
      .catch((e) => {
        if (!mounted) return;
        const msg = e?.message || String(e);
        onError(
          msg.includes('NotAllowed') || msg.includes('Permission')
            ? 'يرجى السماح بالوصول للكاميرا'
            : 'فشل فتح الكاميرا',
        );
      });

    return () => {
      mounted = false;
      const scanner = scannerRef.current;
      if (!scanner) return;
      const cleanup = () => {
        try { scanner.clear(); } catch (_) { /* ignore */ }
        scannerRef.current = null;
        startedRef.current = false;
      };
      try {
        if (startedRef.current) {
          scanner.stop().then(cleanup).catch(cleanup);
        } else {
          cleanup();
        }
      } catch (_) {
        cleanup();
      }
    };
  }, [onResult, onError, continuous]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        maxWidth: 320,
        aspectRatio: '1/1',
        margin: '0 auto',
        background: '#000',
        borderRadius: 12,
        overflow: 'hidden',
        position: 'relative',
      }}
    />
  );
}

export default function QrScanModal({
  open,
  onClose,
  onResult,
  title = 'مسح رمز الطلب',
  subtitle = 'وجّه الكاميرا نحو باركود أو رمز QR على الملصق',
  confirmLabel = 'إضافة',
  continuous = false,
  statusMessage = '',
  busy = false,
  variant = 'company',
}) {
  const [cameraError, setCameraError] = useState('');
  const [manual, setManual] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    setCameraError('');
    setManual('');
    return lockBodyScroll();
  }, [open]);

  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const handleResult = useCallback((text) => {
    onResultRef.current?.(text);
  }, []);

  const handleError = useCallback((msg) => {
    setCameraError(msg);
  }, []);

  function submitManual(e) {
    e?.preventDefault?.();
    if (busy) return;
    const value = manual.trim();
    if (!value) return;
    onResult(value);
    if (continuous) setManual('');
  }

  if (!open) return null;

  if (variant === 'rider') {
    return (
      <div
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        role="presentation"
        style={{
          position: 'fixed', inset: 0, zIndex: 400,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          background: 'rgba(0,0,0,0.72)', padding: 14, boxSizing: 'border-box',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="qr-scan-title"
          style={{
            width: '100%', maxWidth: 420, boxSizing: 'border-box',
            background: RIDER_THEME.card, border: '1px solid ' + RIDER_THEME.border,
            borderRadius: 22, padding: 18, color: RIDER_THEME.text,
            boxShadow: '0 16px 48px rgba(0,0,0,0.55)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
            <div>
              <div id="qr-scan-title" style={{ fontSize: 17, fontWeight: 800 }}>{title}</div>
              {subtitle ? <div style={{ color: RIDER_THEME.textMid, fontSize: 12, marginTop: 4, lineHeight: 1.45 }}>{subtitle}</div> : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="إغلاق"
              style={{
                background: RIDER_THEME.surface, border: '1px solid ' + RIDER_THEME.border,
                color: RIDER_THEME.textMid, width: 36, height: 36, borderRadius: 12,
                fontSize: 18, cursor: 'pointer',
              }}
            >✕</button>
          </div>
          {cameraError ? (
            <p style={{ color: RIDER_THEME.red, textAlign: 'center', margin: '0 0 12px', fontSize: 13 }}>{cameraError}</p>
          ) : (
            <QrScannerMount onResult={handleResult} onError={handleError} continuous={continuous} />
          )}
          {statusMessage ? (
            <p style={{ textAlign: 'center', margin: '12px 0 0', color: RIDER_THEME.textMid, fontSize: 13 }}>{statusMessage}</p>
          ) : null}
          <form onSubmit={submitManual} style={{ marginTop: 14 }}>
            <label htmlFor="qr-manual" style={{ display: 'block', fontSize: 12, color: RIDER_THEME.textMid, marginBottom: 6 }}>
              أو أدخل رقم الطلب / التتبع
            </label>
            <input
              id="qr-manual"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              placeholder="SHP-… أو رمز التتبع"
              dir="ltr"
              autoComplete="off"
              style={{
                width: '100%', boxSizing: 'border-box',
                background: RIDER_THEME.surface, border: '1px solid ' + RIDER_THEME.border,
                color: RIDER_THEME.text, borderRadius: 12, padding: '12px 14px', fontSize: 15,
              }}
            />
          </form>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              style={{
                flex: 1, background: RIDER_THEME.surface, border: '1px solid ' + RIDER_THEME.border,
                color: RIDER_THEME.textMid, borderRadius: 12, padding: '12px', fontWeight: 700, cursor: 'pointer',
              }}
            >إغلاق</button>
            <button
              type="button"
              onClick={submitManual}
              disabled={busy}
              style={{
                flex: 1, background: RIDER_THEME.accent, border: 'none', color: '#000',
                borderRadius: 12, padding: '12px', fontWeight: 800, cursor: 'pointer',
              }}
            >{confirmLabel}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="vi-form-dialog-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="presentation"
    >
      <div
        className="vi-form-dialog"
        style={{ maxWidth: 420, width: '100%' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="qr-scan-title"
      >
        <header className="vi-form-dialog__header">
          <div className="vi-form-dialog__header-inner">
            <span className="vi-form-dialog__header-icon" aria-hidden>📷</span>
            <div className="vi-form-dialog__header-text">
              <h2 id="qr-scan-title" className="vi-form-dialog__title">{title}</h2>
              {subtitle && <p className="vi-form-dialog__subtitle">{subtitle}</p>}
            </div>
          </div>
          <button type="button" className="vi-form-dialog__close" onClick={onClose} aria-label="إغلاق">✕</button>
        </header>
        <div className="vi-form-dialog__body">
          {cameraError ? (
            <p style={{ color: 'var(--color-danger-text)', textAlign: 'center', margin: '0 0 12px' }}>{cameraError}</p>
          ) : (
            <QrScannerMount onResult={handleResult} onError={handleError} continuous={continuous} />
          )}
          {statusMessage ? (
            <p style={{
              textAlign: 'center',
              margin: '12px 0 0',
              color: 'var(--color-text-secondary, #555)',
              fontSize: 13,
            }}>
              {statusMessage}
            </p>
          ) : null}
          <form onSubmit={submitManual} style={{ marginTop: 16 }}>
            <label className="vi-label" htmlFor="qr-manual">أو أدخل رقم الطلب / التتبع</label>
            <input
              id="qr-manual"
              className="vi-input"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              placeholder="SHP-… أو رمز التتبع"
              dir="ltr"
              autoComplete="off"
            />
          </form>
        </div>
        <footer className="vi-form-dialog__footer">
          <button type="button" className="vi-dialog-btn vi-dialog-btn--ghost" onClick={onClose} disabled={busy}>إغلاق</button>
          <button type="button" className="vi-dialog-btn vi-dialog-btn--primary" onClick={submitManual} disabled={busy}>
            {confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
